import {
  BF_GIVE_NOW_FUND,
  BF_PLEDGE_CELEBRATION_FLAG,
  BF_PLEDGE_OPEN_FLAG,
  BUILDING_FUND_PLEDGE_FLAG,
  BF_PLEDGE_AMOUNT_MAX,
  BF_PLEDGE_ERRORS,
  BF_PLEDGE_MONTHS,
  BF_PLEDGE_YEAR,
  BF_BANNER_MIN_AGE,
  BF_FIRST_TIME_MIN_AMOUNT,
  BF_YOUTH_MAX_AGE,
  BF_YOUTH_MONTHLY_CAP,
} from '@/constants/MyGiving/building-fund-pledge';
import { fastStatusPath } from '@/constants/MyGiving/fast-transfer';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { prefersReducedMotion } from '@/utils/unseen-animations';
import {
  ERROR_PATTERN_VALID_NAME,
  ERROR_REQUIRED,
  ERROR_VALID_DATE,
  ERROR_VALID_NRIC,
} from '@/constants/validation/error-messages';
import { REGEX_DATE_ISO, REGEX_NAME, REGEX_NRIC } from '@/constants/validation/regex-patterns';
import { isValidContactNumber, normalizeContactNumber, splitContactNumber } from '@/utils/phone';

// Pure helpers behind the BF2026 pledge form, laid out as the physical card: a
// one-time amount OR five months, AND/OR sow. The form holds what the member typed
// — the amounts as strings, the sow text, three ticks and, for a couple, the
// partner's details — and everything the page decides from that lives here, so it
// can be tested without mounting it.

const AMOUNT_PATTERN = /^(\d+\.?\d*|\.\d+)$/;

/**
 * An amount as the member typed it, as a number of dollars.
 *
 * Commas are ignored, since the field reformats itself to `1,500.00` on blur the
 * way the other MyGiving amount fields do. Returns `null` for a blank field and
 * `NaN` for anything the pledge API would refuse: not a number, negative, or over
 * $99,999,999. Cents past the second place are rounded, as the blur reformat
 * does, so a valid result always has at most two decimals.
 */
export function parseAmount(value) {
  const clean = String(value ?? '')
    .replace(/,/g, '')
    .trim();
  if (clean === '') return null;
  if (!AMOUNT_PATTERN.test(clean)) return NaN;

  const amount = Math.round(Number(clean) * 100) / 100;
  return amount <= BF_PLEDGE_AMOUNT_MAX ? amount : NaN;
}

/**
 * The blur reformat, as the P1 frames write amounts: `1500` → `1,500`, `300.5` →
 * `300.50`. Blank and invalid input is left as typed.
 */
export function formatAmount(value) {
  const amount = parseAmount(value);
  if (amount === null || Number.isNaN(amount)) return value;
  return formatPledgeAmount(amount).slice(1);
}

/**
 * The months for a total the member typed: split evenly to the cent, the odd cents
 * to the earliest months. Blank and invalid totals leave every month blank.
 */
export function splitTotal(total) {
  const amount = parseAmount(total);
  const keys = BF_PLEDGE_MONTHS.map(({ key }) => key);
  if (!amount) return Object.fromEntries(keys.map(key => [key, '']));
  return spread({}, keys, amount);
}

/**
 * The months after the member changes one of them while a total they typed holds:
 * the others move so the five still add up to it. Months the member has typed in
 * (`edited`) stay as typed, so only the rest absorb the change; once every other
 * month has been typed in, they all move. When the months typed in come to more than
 * the total, the rest go to 0 and the total grows to match. An invalid amount in the
 * changed month moves nothing.
 */
export function rebalanceMonths(months, changedKey, total, edited = []) {
  const changed = parseAmount(months[changedKey]);
  if (Number.isNaN(changed)) return months;

  const others = BF_PLEDGE_MONTHS.map(({ key }) => key).filter(key => key !== changedKey);
  const untouched = others.filter(key => !edited.includes(key));
  const targets = untouched.length ? untouched : others;
  const kept = others.filter(key => !targets.includes(key));
  const fixed = sumToCents([changed || 0, ...kept.map(key => parseAmount(months[key]) || 0)]);
  const rest = Math.max(sumToCents([parseAmount(total) || 0, -fixed]), 0);
  return spread({ ...months }, targets, rest);
}

// `amount` split evenly over `keys` to the cent, written into `months` as the
// fields show amounts.
function spread(months, keys, amount) {
  const cents = Math.round(amount * 100);
  const base = Math.floor(cents / keys.length);
  keys.forEach((key, index) => {
    months[key] = formatAmount(String((base + (index < cents % keys.length ? 1 : 0)) / 100));
  });
  return months;
}

// Each month's amount as a number, with blank and invalid months counted as 0.
function monthAmounts(months) {
  // `null` (blank) and `NaN` (invalid) are both falsy.
  return BF_PLEDGE_MONTHS.map(({ key }) => parseAmount(months[key]) || 0);
}

/** The one-time amount as a number, blank and invalid counted as 0. */
export function oneTimeAmount(form) {
  return parseAmount(form.oneTime) || 0;
}

/**
 * Whether the form pledges a one-time amount rather than the months. The two
 * exclude each other on the page, so a one-time amount above 0 decides it.
 */
export function pledgesOneTime(form) {
  return oneTimeAmount(form) > 0;
}

// The amounts the form pledges, blank and invalid counted as 0.
function pledgedAmounts(form) {
  return pledgesOneTime(form) ? [oneTimeAmount(form)] : monthAmounts(form.months);
}

/** The five months added up, to the cent. */
export function monthTotal(months) {
  return sumToCents(monthAmounts(months));
}

function sumToCents(amounts) {
  return amounts.reduce((cents, amount) => cents + Math.round(amount * 100), 0) / 100;
}

/**
 * A pledge amount for display: `$1,500` when it is whole dollars, `$300.50`
 * otherwise. Whole amounts drop the cents as the frames do; a partial one keeps
 * both digits so it never reads `$300.5`.
 */
export function formatPledgeAmount(amount) {
  const digits = Number.isInteger(amount) ? 0 : 2;
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

/**
 * Give now with Building Fund chosen, as D and V open it. `prefill` keeps the
 * amount a one-time pledge put in the store (`myGiving/setGiveNowPrefill`); any
 * other Give now that names the fund drops it.
 */
export function bfGiveNowRoute({ prefill = false } = {}) {
  const query = { fromgivenow: true, fund: BF_GIVE_NOW_FUND };
  if (prefill) query.prefill = 'pledge';
  return { path: '/my-giving/giving-options', query };
}

/** Whether the pledge window is open (BF_PLEDGE_OPEN_FLAG; no row reads as closed). */
export function isBfPledgeOpen() {
  return useFeatureFlags().isEnabled(BF_PLEDGE_OPEN_FLAG);
}

/**
 * Whether a submit gets the celebration (BF_PLEDGE_CELEBRATION_FLAG). Never under
 * reduced motion: the member goes straight to the submitted page, stamp in place.
 */
export function isBfCelebration() {
  return useFeatureFlags().isEnabled(BF_PLEDGE_CELEBRATION_FLAG) && !prefersReducedMotion();
}

/**
 * Whether a one-time FAST gift to `fund` gets the BF2026 screens (the "Continue"
 * after the success animation, then "FAST - Submitted"): a Building Fund gift
 * while the pledge flag is on and the pledge window is open.
 */
export function isBfFastGift(fund) {
  return (
    fund === BF_GIVE_NOW_FUND &&
    useFeatureFlags().isEnabled(BUILDING_FUND_PLEDGE_FLAG) &&
    isBfPledgeOpen()
  );
}

/**
 * Whether a Building Fund gift from Give now plays the building video. Every one
 * does, with no flag: only reduced motion skips it. (The pledge submit's own
 * celebration stays behind BF_PLEDGE_CELEBRATION_FLAG, see isBfCelebration.)
 */
export function playsBfGiftVideo() {
  return !prefersReducedMotion();
}

/**
 * Where a FAST gift to `fund` goes the moment it succeeds, with no generic success
 * animation first. A Building Fund gift goes to its status page with `?celebrate=1`,
 * which plays the building video in place of the hands animation. Under reduced
 * motion it goes to the BF2026 "FAST - Submitted" screen while the pledge flags are
 * on (isBfFastGift), and otherwise gets the generic animation like any other gift.
 * Null for any other gift, which plays the animation and its Back to MyGiving.
 */
export function bfFastSubmittedPath(fund, transactionId) {
  if (!transactionId || fund !== BF_GIVE_NOW_FUND) return null;
  const path = fastStatusPath(transactionId);
  if (playsBfGiftVideo()) return `${path}?celebrate=1`;
  return isBfFastGift(fund) ? path : null;
}

/**
 * A FAST gift's amount on the BF2026 "FAST - Submitted" screen, as its frame has
 * it: `$1000` when whole dollars, `$300.50` otherwise — no thousands separator,
 * unlike the pledge's `formatPledgeAmount`. Blank for anything not a number.
 */
export function formatGiftAmount(amount) {
  if (amount === null || amount === undefined || amount === '') return '';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '';
  return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
}

/**
 * What the done page (D) and the view page (V) show for a pledge. Takes either
 * the body the member submitted or the pledge giving-api returns: both carry
 * `bf`, `sow_pledge`, `is_youth`, `youth_exceeded` and `couplePledge`, but the body sends a
 * blank month as 0 and the API reads it back as null, so both count as 0 here. A
 * one-time pledge is `bf.one_time_commitment`, with no months.
 *
 * On a spouse's verified couple pledge (`secondary`) the API returns the pledge as
 * the other spouse made it, so its `partner_name` is the viewer's own name. Their
 * partner is the one who submitted it, `full_name` — as TableBF.vue shows it.
 */
export function pledgeSummary(pledge) {
  const bf = pledge?.bf || {};
  const months = BF_PLEDGE_MONTHS.map(({ key, label }) => ({
    key,
    label,
    amount: Number(bf[key]) || 0,
  }));
  const partnerName = pledge?.secondary ? pledge.full_name : pledge?.couplePledge?.partner_name;
  return {
    months,
    total: sumToCents(months.map(({ amount }) => amount)),
    oneTime: Number(bf.one_time_commitment) || 0,
    sow: pledge?.sow_pledge?.trim() || null,
    couple: pledge?.couplePledge ? { partnerName: partnerName || null } : null,
    youth: Boolean(pledge?.is_youth),
    // null from giving-api means it was not asked; only a tick counts.
    firstTime: pledge?.first_four_digit_pledge === true,
    // giving-api stores it only on a youth's pledge; read it the same way.
    youthExceeded: Boolean(pledge?.is_youth && pledge?.youth_exceeded),
  };
}

/**
 * Whether V keeps the pledge's details from the member. A youth checks what they
 * submitted on D, straight after submitting, but cannot see it again once they
 * leave (Yi Kai, 2026-09-30; the web pledge form hid "Review Pledge" from them too).
 * A youth by age now, or a pledge sent as a youth's — which covers the tick. A
 * spouse's view carries no youth flag, so only age applies there.
 */
export function hidesPledgeFromYouth(pledge, profile) {
  return Boolean(pledge?.is_youth) || isYouthByAge(profile);
}

/**
 * Whether the member pledges as a youth by age, whatever the tick says — as the web
 * pledge form (building-fund-frontend) decides it from building-fund-api's
 * `ageIsAbove18`: 16 or younger by birth year (not birthday), or no date of birth
 * on record at all.
 *
 * Takes the profile store's state. Until the profile has loaded it has no date of
 * birth either, which must not make everyone a youth, so a profile with no
 * `givenName` — the marker `fetchProfile` itself uses — is not one. The date is kept
 * formatted ("Wednesday, 31 Jan 1990"), which not every WebView's `Date` parses, so
 * only the year is read.
 */
export function isYouthByAge(profile, today = new Date()) {
  if (!profile?.givenName) return false;
  const age = ageByBirthYear(profile, today);
  return age === null || age <= BF_YOUTH_MAX_AGE;
}

/**
 * Whether the member is shown the landing-page banner: 18 or older by birth year.
 * False until the profile has loaded and false with no date of birth, so the
 * banner never flashes in front of a minor.
 */
export function isAdultByAge(profile, today = new Date()) {
  if (!profile?.givenName) return false;
  const age = ageByBirthYear(profile, today);
  return age !== null && age >= BF_BANNER_MIN_AGE;
}

/** Years since the profile's birth year, or null with no readable year on record. */
function ageByBirthYear(profile, today) {
  const year = Number(String(profile.dateOfBirth ?? '').match(/\b(\d{4})\b/)?.[1]);
  return year ? today.getFullYear() - year : null;
}

/** Whether the pledge goes in as a youth's: the box is ticked, or the member is one by age. */
export function pledgesAsYouth(form, youthByAge = false) {
  return form.isYouth || youthByAge;
}

/**
 * Whether the form asks "I'm giving for the first time": a youth (by the tick or
 * by age) whose one-time amount, or five months added up, is a four-digit pledge
 * ($1,000 or more) — under the one-time card or in the months card, where the
 * frames draw the tick (P1a, P1b). giving-api checks neither youth nor amount, so
 * the body carries the answer only when it was asked.
 */
export function asksFirstTime(form, youthByAge = false) {
  const amount = pledgesOneTime(form) ? oneTimeAmount(form) : monthTotal(form.months);
  return pledgesAsYouth(form, youthByAge) && amount >= BF_FIRST_TIME_MIN_AMOUNT;
}

/**
 * Whether a youth's pledge goes over the monthly cap: any month, or the one-time
 * amount, which is given within a month. That needs parental consent, so the form
 * flags it and the pledge still goes through (D18).
 */
export function youthExceeded(form, youthByAge = false) {
  return (
    pledgesAsYouth(form, youthByAge) &&
    pledgedAmounts(form).some(amount => amount > BF_YOUTH_MONTHLY_CAP)
  );
}

/** A date as `YYYY-MM-DD` in the device's own calendar, the form the date picker uses. */
export function isoDate(date) {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// A `YYYY-MM-DD` that names a real day: the pattern alone lets 31-02 through.
function isRealIsoDate(value) {
  if (!REGEX_DATE_ISO.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * The partner fields' problems, one message per field (`name`, `dob`, `last4`),
 * with the app's own profile wording. The date comes from the date picker as
 * `YYYY-MM-DD`; `today` is a parameter so the future-date rule can be tested.
 */
export function partnerErrors(form, today = new Date()) {
  const errors = {};

  const name = form.partnerName.trim();
  if (!name) errors.name = ERROR_REQUIRED;
  else if (!REGEX_NAME.test(name)) errors.name = ERROR_PATTERN_VALID_NAME;

  const dob = form.partnerDob;
  if (!dob) errors.dob = ERROR_REQUIRED;
  else if (!isRealIsoDate(dob)) errors.dob = ERROR_VALID_DATE;
  else if (dob > isoDate(today)) errors.dob = BF_PLEDGE_ERRORS.dobFuture;

  const last4 = form.partnerLast4.trim();
  if (!last4) errors.last4 = ERROR_REQUIRED;
  else if (!REGEX_NRIC.test(last4)) errors.last4 = ERROR_VALID_NRIC;

  return errors;
}

/**
 * The partner's personal ID, as giving-api matches a couple pledge on it: the last 4
 * of their NRIC/FIN/Passport, uppercased, then the day and month of their birth —
 * `123A` born 1990-01-31 is `123A3101`, the format PayNow and giving-api build for
 * a member. `null` until both parts are valid.
 */
export function partnerPersonalId(last4, dob) {
  const chars = String(last4 ?? '').trim();
  if (!REGEX_NRIC.test(chars) || !isRealIsoDate(dob)) return null;
  const [, month, day] = dob.split('-');
  return `${chars.toUpperCase()}${day}${month}`;
}

/**
 * What stops the form being submitted, as an object with a key per problem:
 * `amounts` lists the amount fields that are not a valid amount (`oneTime` and the
 * month keys); `empty` means nothing was pledged at all — no one-time amount, no
 * month above 0 and no sow text; `partner` holds the partner
 * fields' messages when pledging as a couple; `consent` means "I consent" is not
 * ticked; `mobile` means the member typed a mobile number that is not a real one. An empty object means the pledge can be submitted.
 */
export function validatePledgeForm(form) {
  const errors = {};

  const amounts = BF_PLEDGE_MONTHS.map(({ key }) => key).filter(key =>
    Number.isNaN(parseAmount(form.months[key])),
  );
  if (Number.isNaN(parseAmount(form.oneTime))) amounts.unshift('oneTime');
  if (amounts.length) errors.amounts = amounts;

  if (!pledgesOneTime(form) && monthTotal(form.months) === 0 && !form.sow.trim()) {
    errors.empty = true;
  }
  if (form.couple) {
    const partner = partnerErrors(form);
    if (Object.keys(partner).length) errors.partner = partner;
  }
  if (!form.consent) errors.consent = true;
  if (mobileInvalid(form)) errors.mobile = true;

  return errors;
}

// Only a number the member typed is checked: the one the profile pre-filled goes as
// it is, and an emptied one falls back to it (giving-api does the same).
function mobileInvalid({ details, detailsPrefill }) {
  const edited = normalizeContactNumber(details?.mobileNumber);
  if (!edited || edited === normalizeContactNumber(detailsPrefill?.mobileNumber)) return false;
  const { dialCode, nationalNumber } = splitContactNumber(edited);
  return !isValidContactNumber(dialCode, nationalNumber);
}

/**
 * The form as the pledge API's `PledgeBodyDto`. Amounts go as numbers, never strings
 * — the API validates without transforming — and a blank month goes as 0. A
 * one-time pledge sends `bf.one_time_commitment` alone, with no month keys. The sow
 * text is `sow_pledge`, beside `bf` rather than in it, and blank goes as null.
 *
 * A couple pledge names the partner, their personal ID, last 4 and date of birth. The API refuses one
 * without a name, so with the box ticked but no name the body goes without it
 * (Submit's own checks stop that form first).
 */
export function toPledgeBody(form, { youthByAge = false } = {}) {
  const bf = {};
  if (pledgesOneTime(form)) {
    bf.one_time_commitment = oneTimeAmount(form);
  } else {
    monthAmounts(form.months).forEach((amount, index) => {
      bf[BF_PLEDGE_MONTHS[index].key] = amount;
    });
  }

  return {
    year: BF_PLEDGE_YEAR,
    sabbatical: false,
    help: false,
    tithe: false,
    tithe_continue: false,
    is_youth: pledgesAsYouth(form, youthByAge),
    youth_exceeded: youthExceeded(form, youthByAge),
    bf,
    sow_pledge: form.sow.trim() || null,
    // Left out when not asked, which giving-api stores as null ("not asked").
    ...(asksFirstTime(form, youthByAge) && { first_four_digit_pledge: Boolean(form.firstTime) }),
    couplePledge: couplePledgeBody(form),
    ...memberDetailsBody(form),
  };
}

// The pledge card's name, mobile and CG: what the member typed, or what the page
// pre-filled from the profile when they did not edit it. They go on the pledge only
// and never touch the profile. A field cleared falls back to its pre-filled value,
// so a pledge is not recorded with no name on it; one with nothing either way (no
// number on file) is left out.
const MEMBER_DETAIL_KEYS = {
  fullName: 'full_name',
  mobileNumber: 'contact_number',
  connectGroup: 'connect_group',
};

// The mobile goes as `+<calling code> <national number>` (`+65 91234567`), the format
// giving-api and the MyGiving portal share, however it was typed or stored.
const MEMBER_DETAIL_FORMAT = {
  mobileNumber: normalizeContactNumber,
};

function memberDetailsBody({ details, detailsPrefill }) {
  const body = {};
  Object.entries(MEMBER_DETAIL_KEYS).forEach(([field, key]) => {
    const format = MEMBER_DETAIL_FORMAT[field] || (value => value.trim());
    const value = format(details?.[field] || '') || format(detailsPrefill?.[field] || '');
    if (value) body[key] = value;
  });
  return body;
}

// The last 4 and date of birth also go on their own (giving-api stores them as the
// partner's extra identifiers), each only once valid: the API refuses a bad one.
function couplePledgeBody(form) {
  const name = form.partnerName.trim();
  if (!form.couple || !name) return null;
  const last4 = form.partnerLast4.trim();
  const dob = form.partnerDob;
  return {
    partner_name: name,
    partner_personal_id: partnerPersonalId(last4, dob),
    ...(REGEX_NRIC.test(last4) && { partner_uin: last4.toUpperCase() }),
    ...(isRealIsoDate(dob) && { partner_dob: dob }),
  };
}

/** A blank form: every amount empty, nothing ticked, no partner details. */
export function emptyPledgeForm() {
  return {
    oneTime: '',
    months: Object.fromEntries(BF_PLEDGE_MONTHS.map(({ key }) => [key, ''])),
    sow: '',
    couple: false,
    partnerName: '',
    partnerDob: '',
    partnerLast4: '',
    isYouth: false,
    // "I'm giving for the first time"; sent only while the form asks it.
    firstTime: false,
    consent: false,
    // The name, mobile and CG once "Edit details" is tapped (null until then), and
    // what the page pre-filled them with, which is sent when they are not edited.
    details: null,
    detailsPrefill: null,
  };
}
